'use client';

import { moreLinks } from '@/constants';
import { cn } from '@/lib/utils';
import { PathEnum } from '@/types/enums';
import { Tally3, X } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import Link from '../ui/Link';
import Logotype from '../ui/Logotype';
import Dropdown from '../ui/MoreDropdown';
import LocaleSwitcher from './LocaleSwitcher';

export default function MobileHeaderMenu({
    className,
    color,
}: {
    className?: string;
    color: 'black' | 'white';
}) {
    const t = useTranslations();
    const locale = useLocale();

    const isBlack = color === 'black';

    const linkStyles =
        'text-white lowercase text-[34px] font-light font-helveticaNeue leading-none';

    const [isMobileMenuOpened, setIsMobileMenuOpened] = useState<boolean>(false);

    const toggleMenu = () => setIsMobileMenuOpened((prev) => !prev);

    const closeMenu = () => setIsMobileMenuOpened(false);

    useEffect(() => {
        if (isMobileMenuOpened) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isMobileMenuOpened]);

    return (
        <div className={className}>
            <Tally3
                strokeWidth={1.5}
                width={60}
                height={60}
                className={cn(
                    'rotate-90 text-white absolute right-0 top-0 pl-3',
                    isBlack && 'bg-gray-300/60',
                )}
                onClick={toggleMenu}
            />
            <div
                className={cn(
                    'fixed w-screen h-dvh top-0 left-0 py-2 px-5 hidden max-lg:flex flex-col transition-all translate-x-[100vw] overflow-y-auto z-30 bg-hero-pattern bg-gray-300 bg-no-repeat bg-cover bg-center pb-20 overflow-hidden',
                    isMobileMenuOpened && 'translate-x-0',
                )}
            >
                <Image className="z-negative" src="/images/hero-bg.webp" alt="Background" fill />

                <Image
                    src="/images/shadow-1.png"
                    alt="Shadow 1"
                    width={880}
                    height={710}
                    className="absolute top-1/6 left-0 shadow-animate-1 hidden lg:block -z-1"
                />
                <Image
                    src="/images/shadow-1.png"
                    alt="Shadow 1"
                    width={410}
                    height={300}
                    className="lg:hidden absolute top-1/6 -left-1/2 shadow-animate-1 -z-1"
                />
                <Image
                    src="/images/shadow-2.png"
                    alt="Shadow 2"
                    width={360}
                    height={400}
                    className="absolute bottom-1/3 right-0 shadow-animate-2 hidden lg:block -z-1"
                />
                <Image
                    src="/images/shadow-2.png"
                    alt="Shadow 2"
                    width={310}
                    height={250}
                    className="lg:hidden absolute bottom-1/4 -right-1/4 shadow-animate-2 -z-1"
                />
                <Link
                    className="relative w-[100px] h-[92px]"
                    href={PathEnum.Home}
                    onClick={closeMenu}
                >
                    <Logotype color="white" />
                </Link>
                <X
                    className="absolute right-0 top-0 text-white bg-gray-300/60"
                    width={60}
                    height={60}
                    strokeWidth={1.5}
                    onClick={toggleMenu}
                />
                <div className="flex flex-col mt-12 gap-7 overflow-auto max-h-screen h-full">
                    <Link
                        href={PathEnum.About}
                        className={linkStyles}
                        title={t('Header.about')}
                        onClick={closeMenu}
                    />
                    <Link
                        href={PathEnum.Watch}
                        className={linkStyles}
                        title={t('Header.watch')}
                        onClick={closeMenu}
                    />
                    <Link
                        href={PathEnum.Discover}
                        className={linkStyles}
                        title={t('Header.discover')}
                        onClick={closeMenu}
                    />
                    <Link
                        href={PathEnum.YourStory}
                        className={linkStyles}
                        title={t('Header.yourStory')}
                        onClick={closeMenu}
                    />
                    <Link
                        href={
                            locale === 'fr'
                                ? 'https://docs.google.com/forms/d/1J8dghOAjJxbmIUaQgC-hIpjx8kkSEzkTmXfIVypFSWA'
                                : 'https://docs.google.com/forms/d/1PZDOtmeNa8wJXCWSDB_sfwOl5FUAR3Jlyc3gRr9N3E8'
                        }
                        target="_blank"
                        className={linkStyles}
                        title={t('Header.getInvolved')}
                        onClick={closeMenu}
                    />
                    <Dropdown
                        title={t('Buttons.more')}
                        items={moreLinks(t)}
                        orientation="left"
                        color="white"
                        menuClassName="max-lg:gap-7 max-lg:mt-7"
                        onCloseMenu={closeMenu}
                    />
                    <div className="mt-auto">
                        <LocaleSwitcher color="white" />
                    </div>
                </div>
            </div>
        </div>
    );
}
